/**
 * Event Tracker Utilities
 * Centralized event tracking system for Shopify theme
 * Optimized for performance with debouncing and lazy loading
 */

(function () {
  'use strict';

  // Utility: Debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Storage wrapper with cookie fallback
  const storage = {
    get: function (key) {
      try {
        const value = localStorage.getItem(key);
        if (value !== null) return value;
      } catch (e) {
        console.warn('localStorage not available, trying cookie fallback');
      }
      return this.getCookie(key);
    },

    set: function (key, value) {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        if (e.name === 'QuotaExceededError') {
          this.cleanup();
          try {
            localStorage.setItem(key, value);
            return true;
          } catch (e2) {
            return this.setCookie(key, value, 365);
          }
        }
        return this.setCookie(key, value, 365);
      }
    },

    remove: function (key) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        this.setCookie(key, '', -1);
      }
    },

    getCookie: function (name) {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    },

    setCookie: function (name, value, days) {
      let expires = '';
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
      return true;
    },

    cleanup: function () {
      // Remove old tracking data to free up space
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('old_') || key.includes('temp_') || key.includes('_expired'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
        console.log('Storage cleanup completed');
      } catch (e) {
        console.warn('Storage cleanup failed:', e);
      }
    },
  };

  // Event Tracker
  window.EventTracker = {
    events: {},
    
    /**
     * Trigger an event after a delay
     * @param {string} eventName - Name of the event
     * @param {object} data - Event data
     * @param {number} delay - Delay in milliseconds (default: 5000)
     */
    trigger: function (eventName, data, delay) {
      delay = delay || 5000;
      
      if (this.events[eventName]) {
        console.log('Event already triggered:', eventName);
        return;
      }
      
      this.events[eventName] = true;
      console.log('Scheduling event:', eventName, 'in', delay / 1000, 'seconds');
      
      setTimeout(() => {
        this.dispatch(eventName, data);
      }, delay);
    },

    /**
     * Dispatch an event immediately
     * @param {string} eventName - Name of the event
     * @param {object} data - Event data
     */
    dispatch: function (eventName, data) {
      console.log('✓ Dispatching event:', eventName);
      
      // Try jQuery first (OptiMonk and many tracking tools prefer this)
      if (typeof $ !== 'undefined') {
        $('html').trigger(eventName, data);
      } else if (typeof jQuery !== 'undefined') {
        jQuery('html').trigger(eventName, data);
      }
      
      // Also dispatch native CustomEvent for modern listeners
      if (window.CustomEvent) {
        const event = new CustomEvent(eventName, {
          detail: data,
          bubbles: true,
        });
        window.dispatchEvent(event);
      }
    },

    /**
     * Reset a specific event (for testing)
     * @param {string} eventName - Name of the event to reset
     */
    reset: function (eventName) {
      if (eventName) {
        this.events[eventName] = false;
        console.log('Reset event:', eventName);
      } else {
        this.events = {};
        console.log('Reset all events');
      }
    },
  };

  // Expose storage utility globally
  window.ThemeStorage = storage;

  // Log initialization
  console.log('Event Tracker initialized');
})();